/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

// These seem like something that should come from the channels.templates
// array.  Need to investigate better way to handle these.
import { ApiStatus } from '../../../../ts';
import { RootState } from '../../../../ts/state';
import { Voice } from '../../../../ts/text-to-speech';
import { selectAssistantLanguage } from '../../../ia/store/assistants/core-selectors';
import { selectIsGenesysAudioConnectorInCpd } from '../../../ia/store/features/selectors';
import { selectInstance } from '../../../ia/store/instance/selectors';
import { isWxoEnabled } from '../../../ia/util/config';
import {
  DEFAULT_JAPANESE_SPEECH_TO_TEXT_MODEL,
  DEFAULT_ENGLISH_US_TEXT_TO_SPEECH_MODEL,
  DEFAULT_ENGLISH_US_TEXT_TO_SPEECH_MODEL_CPD,
  DEFAULT_ENGLISH_SPEECH_TO_TEXT_MODEL
} from '../../components/phone/constants';
import {
  filterByLanguage,
  filterByNonDeprecatedModel,
  buildInstanceSorter,
  buildPreferredFilter,
  sortModels,
  sortVoices,
  parseVoice,
  getDefaultSTTModelByLanguage
} from '../../helpers/speech-helpers';
import {
  getDefaultTTSModelByLanguage,
  getTTSModelLanguageWithCountryDescription
} from '../../helpers/text-helpers';
import {
  speechToTextInstancesAdapter,
  textToSpeechInstancesAdapter
} from './adapters';
import { Voice as messages } from './messages';

const speechToTextInstancesSelector = speechToTextInstancesAdapter.getSelectors(
  (state: RootState) => state.voice?.speechToText
);

const textToSpeechInstancesSelector = textToSpeechInstancesAdapter.getSelectors(
  (state: RootState) => state.voice?.textToSpeech
);

export const selectAllSpeechToTextInstances =
  speechToTextInstancesSelector.selectAll;
export const selectSpeechToTextInstanceById =
  speechToTextInstancesSelector.selectById;
export const selectAllTextToSpeechInstances =
  textToSpeechInstancesSelector.selectAll;
export const selectTextToSpeechInstanceById =
  textToSpeechInstancesSelector.selectById;

export const selectCurrentSpeechToTextInstance = (state: RootState) =>
  state.voice.speechToText.currentInstance;

export const selectCurrentTextToSpeechInstance = (state: RootState) =>
  state.voice.textToSpeech.currentInstance;

export const selectSpeechToTextListIsLoading = (state: RootState) =>
  state.voice.speechToText.listStatus === ApiStatus.LOADING;

export const selectSpeechToTextListLoaded = (state: RootState) =>
  state.voice.speechToText.listStatus === ApiStatus.SUCCEEDED;

export const selectSpeechToTextCreateStatus = (state: RootState) =>
  state.voice.speechToText.createStatus;

export const selectSpeechToTextCreateError = (state: RootState) =>
  state.voice.speechToText.createError;

export const selectSpeechToTextListLoadFailed = (state: RootState) =>
  state.voice.speechToText.listStatus === ApiStatus.ERROR;

export const selectTextToSpeechListIsLoading = (state: RootState) =>
  state.voice.textToSpeech.listStatus === ApiStatus.LOADING;

export const selectTextToSpeechListLoaded = (state: RootState) =>
  state.voice.textToSpeech.listStatus === ApiStatus.SUCCEEDED;

export const selectTextToSpeechCreateStatus = (state: RootState) =>
  state.voice.textToSpeech.createStatus;

export const selectTextToSpeechCreateError = (state: RootState) =>
  state.voice.textToSpeech.createError;

export const selectTextToSpeechListLoadFailed = (state: RootState) =>
  state.voice.textToSpeech.listStatus === ApiStatus.ERROR;

export const selectSpeechToTextInstanceIsLoading = (state: RootState) =>
  state.voice.speechToText.loadStatus === ApiStatus.LOADING;

export const selectSpeechToTextInstanceLoadFailed = (state: RootState) =>
  state.voice.speechToText.loadStatus === ApiStatus.ERROR;

export const selectSpeechToTextInstanceLoaded = (state: RootState) =>
  state.voice.speechToText.loadStatus === ApiStatus.SUCCEEDED;

export const selectTextToSpeechInstanceIsLoading = (state: RootState) =>
  state.voice.textToSpeech.loadStatus === ApiStatus.LOADING;

export const selectTextToSpeechInstanceLoadFailed = (state: RootState) =>
  state.voice.textToSpeech.loadStatus === ApiStatus.ERROR;

export const selectTextToSpeechInstanceLoaded = (state: RootState) =>
  state.voice.textToSpeech.loadStatus === ApiStatus.SUCCEEDED;

const selectSortedAndFilteredInstances = (selector, state: RootState) => {
  const instance = selectInstance(state);
  // For WXO, we are returning exactly one STT and TTS instance from our server from getALLInstnaces call
  // Those instnaces only has instance id and credentials set and nothing else, so we do not need this filter for wxo
  if (!instance?.account_id || !instance?.resource_group_id || isWxoEnabled) {
    // if no instance, or it is in wxo, we can't sort or filter... just return them all.
    return selector(state);
  }
  const { account_id, resource_group_id, region } = instance;
  const preferredFilter = buildPreferredFilter({
    accountId: account_id,
    regionName: region
  });
  const instanceSorter = buildInstanceSorter({
    accountId: account_id,
    resourceGroupId: resource_group_id
  });
  return selector(state).filter(preferredFilter).sort(instanceSorter);
};

export const selectTextToSpeechInstances = (state: RootState) =>
  selectSortedAndFilteredInstances(selectAllTextToSpeechInstances, state);

export const selectPreferredTextToSpeechInstance = (state: RootState) => {
  const list = selectTextToSpeechInstances(state);
  return list ? list[0] : undefined;
};

export const selectSpeechToTextInstances = (state: RootState) =>
  selectSortedAndFilteredInstances(selectAllSpeechToTextInstances, state);

export const selectPreferredSpeechToTextInstance = (state: RootState) => {
  const list = selectSpeechToTextInstances(state);
  return list ? list[0] : undefined;
};

export const selectSpeechToTextModels = (state: RootState) => {
  const regex = / model.+$/;

  return sortModels(
    state.voice?.speechToText.models
      ? state.voice?.speechToText.models.map(model => {
          const newModel = { ...model };
          newModel.friendly_name = newModel.description
            ? newModel.description.replace(regex, '').trim()
            : null;
          return newModel;
        })
      : []
  );
};

export const selectNonDeprecatedSpeechToTextModels = (state: RootState) =>
  selectSpeechToTextModels(state).filter(
    filterByNonDeprecatedModel(DEFAULT_JAPANESE_SPEECH_TO_TEXT_MODEL)
  );

export const selectModelsFilteredByAssistantLanguage = (state: RootState) => {
  const language = selectAssistantLanguage(state);
  let languageModel = '';
  if (language === 'ja') {
    languageModel = DEFAULT_JAPANESE_SPEECH_TO_TEXT_MODEL;
  }
  return selectSpeechToTextModels(state)
    .filter(filterByLanguage(language))
    .filter(filterByNonDeprecatedModel(languageModel));
};

export const selectSpeechToTextModel = (state: RootState, sttModel: string) => {
  const models = selectSpeechToTextModels(state);
  return models.find(model => model?.name === sttModel);
};

export const selectSpeechToTextFilteredModel = (
  state: RootState,
  sttModel: string
) => {
  const models = selectModelsFilteredByAssistantLanguage(state);
  return models.find(model => model?.name === sttModel);
};

export const selectDefaultSpeechToTextModel = (state: RootState) => {
  const assistantLanguage = selectAssistantLanguage(state);

  // This selects the default stt model based on assistant language and based on lsm flag and avilability
  // it selects lsm model as a default one.
  const defaultSttModel = getDefaultSTTModelByLanguage(assistantLanguage);

  const isDefaultSttModelAvailable =
    state.voice?.speechToText.models.filter(
      model => model?.name === defaultSttModel
    ).length !== 0;
  const isEnglishSttModelAvailable =
    state.voice?.speechToText.models.filter(
      model => model?.name === DEFAULT_ENGLISH_SPEECH_TO_TEXT_MODEL
    ).length !== 0;

  return isDefaultSttModelAvailable
    ? defaultSttModel
    : isEnglishSttModelAvailable
    ? DEFAULT_ENGLISH_SPEECH_TO_TEXT_MODEL
    : '';
};

export const selectTextToSpeechVoices = (state: RootState) => {
  // return an empty array.
  return sortVoices(
    state.voice?.textToSpeech?.voices.length > 1
      ? state.voice?.textToSpeech.voices.map(voice => {
          const newVoice = { ...voice };
          // Determine the Voice Version
          const version = /V3/.test(newVoice.name)
            ? 'v3'
            : /V2/.test(newVoice.name)
            ? 'v2'
            : 'v1';
          const { persona, englishDescription, localeDescription } = parseVoice(
            newVoice.description
          );
          // Add a Friendly Name as follows:
          // Isabela: Brazilian Portuguese (português brasileiro) female voice. Dnn technology.
          // --> Brazilian Portuguese (Português Brasileiro): Isabela, female, v3
          const isExpressive = voice.name.includes('Expressive');
          const friendlyNameVersion = isExpressive ? 'expressive' : version;
          newVoice.friendly_name = `${englishDescription}${
            localeDescription ? ' (' + localeDescription + ')' : ''
          }: ${persona}, ${voice.gender}, ${friendlyNameVersion}`;
          return newVoice;
        })
      : []
  );
};

export const selectTextToSpeechVoicesByLanguage = (
  state: RootState,
  language: string
) => {
  let containsExpressiveOrV3 = false;
  const country = getTTSModelLanguageWithCountryDescription(language);
  const voices = sortVoices(
    state.voice?.textToSpeech?.voices.length > 1
      ? state.voice?.textToSpeech.voices
          .filter(voice => {
            const newVoice = { ...voice };
            const newVoiceCountry = getTTSModelLanguageWithCountryDescription(
              newVoice.language
            );

            return newVoiceCountry === country;
          })
          .map(voice => {
            const newVoice = { ...voice };
            // Determine the Voice Version
            const version = /V3/.test(newVoice.name)
              ? 'v3'
              : /V2/.test(newVoice.name)
              ? 'v2'
              : 'v1';
            const { persona, englishDescription, localeDescription } =
              parseVoice(newVoice.description);
            // Add a Friendly Name as follows:
            // Isabela: Brazilian Portuguese (português brasileiro) female voice. Dnn technology.
            // --> Brazilian Portuguese (Português Brasileiro): Isabela, female, v3
            const isExpressive = voice.name.includes('Expressive');
            const friendlyNameVersion = isExpressive ? 'expressive' : version;

            if (isExpressive || /V3/.test(newVoice.name)) {
              containsExpressiveOrV3 = true;
            }
            newVoice.friendly_name = `${englishDescription}${
              localeDescription ? ' (' + localeDescription + ')' : ''
            }: ${persona}, ${voice.gender}, ${friendlyNameVersion}`;
            return newVoice;
          })
      : []
  );
  if (containsExpressiveOrV3) {
    return voices.filter(
      (voice: Voice) =>
        /v3/.test(voice?.friendly_name) ||
        voice?.friendly_name.includes('expressive')
    );
  }

  return voices;
};

export const selectVoicesFilteredByAssistantLanguage = (state: RootState) => {
  const language = selectAssistantLanguage(state);
  return selectTextToSpeechVoices(state).filter(filterByLanguage(language));
};

export const selectTextToSpeechVoice = (state: RootState, voice: string) => {
  const voices = selectTextToSpeechVoices(state);
  return voices.find(model => model?.name === voice);
};

export const selectTextToSpeechFilteredVoice = (
  state: RootState,
  voice: string
) => {
  const filteredVoices = selectVoicesFilteredByAssistantLanguage(state);
  return filteredVoices.find(model => model?.name === voice);
};

export const selectDefaultTextToSpeechModel = (state: RootState) => {
  const assistantLanguage = selectAssistantLanguage(state);
  const isGenesysAudioConnectorEnabledInCpd =
    selectIsGenesysAudioConnectorInCpd(state);

  const defaultTtsModel = getDefaultTTSModelByLanguage(
    assistantLanguage,
    isGenesysAudioConnectorEnabledInCpd
  );

  const isDefaultTtsModelAvailable =
    state.voice?.textToSpeech.voices.filter(
      voice => voice?.name === defaultTtsModel
    ).length !== 0;
  const isEnglishTtsModelAvailable =
    state.voice?.textToSpeech.voices.filter(
      voice =>
        voice?.name ===
        (isGenesysAudioConnectorEnabledInCpd
          ? DEFAULT_ENGLISH_US_TEXT_TO_SPEECH_MODEL_CPD
          : DEFAULT_ENGLISH_US_TEXT_TO_SPEECH_MODEL)
    ).length !== 0;

  return isDefaultTtsModelAvailable
    ? defaultTtsModel
    : isEnglishTtsModelAvailable
    ? DEFAULT_ENGLISH_US_TEXT_TO_SPEECH_MODEL
    : '';
};

export const selectCustomVoices = (state: RootState) => {
  const instance = selectCurrentTextToSpeechInstance(state);
  return instance?.customizations ?? [];
};

export const selectCustomLanguageModels = (state: RootState) => {
  const instance = selectCurrentSpeechToTextInstance(state);
  return (
    instance?.language_customizations?.filter(
      item => item.status === 'available'
    ) ?? []
  );
};

export const selectCustomAcousticModels = (state: RootState) => {
  const instance = selectCurrentSpeechToTextInstance(state);
  return (
    instance?.acoustic_customizations?.filter(
      item => item.status === 'available'
    ) ?? []
  );
};

export const selectElevenLabsModels = (state: RootState) => {
  const assistantLanguage = selectAssistantLanguage(state);
  const models = state.voice.textToSpeechModelsElevenLabs?.models || [];

  // Filter out non-TTS models, excluded IDs, and those lacking the assistant's language
  const excludedIds = ['eleven_multilingual_v1', 'eleven_monolingual_v1'];
  const filteredModels = models.filter(
    model =>
      model?.can_do_text_to_speech &&
      !excludedIds.includes(model?.model_id) &&
      model?.languages?.some(lang => lang.language_id === assistantLanguage)
  );

  return filteredModels;
};

export const selectElevenLabsModelsErrors = (state: RootState) => {
  return state.voice.textToSpeechModelsElevenLabs?.error?.message;
};

export const selectIsElevenLabsModelsLoading = (state: RootState) =>
  state.voice.textToSpeechModelsElevenLabs?.loading;

export const selectElevenLabsVoices = (state: RootState) => {
  return state.voice.textToSpeechVoicesElevenLabs?.voices || [];
};

export const selectElevenLabsVoicesLoading = (state: RootState) =>
  state.voice.textToSpeechVoicesElevenLabs?.loading;
/**
 * selectCreateStatus -- get the InlineLoading props for the current createStatus
 *
 * @param {Object} state redux state
 * @param {Object} intl intl object from `useIntl`
 * @param {function} selector stt or tts create status selector
 * @returns {Object} status object with InlineLoading props
 */
export const selectCreateStatus = (state: RootState, intl, selector) => {
  const status = {
    status: 'inactive',
    'aria-live': 'off',
    description: ''
  } as any;

  const createStatus = selector(state);

  switch (createStatus) {
    case ApiStatus.LOADING:
      status.status = 'active';
      status['aria-live'] = 'assertive';
      status.description = intl.formatMessage(messages.statusCreating);
      break;
    case ApiStatus.SUCCEEDED:
      status.status = 'finished';
      status.description = intl.formatMessage(messages.statusSuccess);
      break;
    case ApiStatus.ERROR:
      status.status = 'error';
      status.description = intl.formatMessage(messages.statusFailed);
      break;
    default:
  }
  return status;
};

export const selectSttUrl = (state: RootState) =>
  state.voice.speechToTextUrl.value;
export const selectTtsUrl = (state: RootState) =>
  state.voice.textToSpeechUrl.value;
